import { pageableDataConverter } from 'data/converters/pagableData.converters';
import { addRecipientsConverter, deleteRecipientConverter, editRecipientDetailsConverter, generatePetChargeReportConverter, recipientCandidateConverter, scheduleItemConverter } from 'data/converters/reporting.converters';
import { TPageableDataWithContent, TPageableDataWithContentUi, TPageableDataParamUI } from 'data/types/pagableData.type';
import { TAddRecipientsUI, TDeleteRecipientUI, TEditRecipientDetailsUI, TGetRecipientCandidatesParamsUI, TPetChargeReportUI, TRecipientCandidateDB, TRecipientCandidateUI, TScheduleItemDB, TScheduleItemUI } from 'data/types/reporting.types';
import { EReportingUrl } from 'data/urls/reporting.url';
import { authSplitApi } from 'redux/helpers/slice.helpers';
import { EPropertyMangerUrl } from 'data/urls/propertyManager.url';
import { queryParamConverter } from 'data/converters/general.converters';

const baseReportsUrl = `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EReportingUrl.REPORTS}`;

export const reportingApi = authSplitApi('reportingApi', [
    'schedules',
    'recipientCandidates',
]).injectEndpoints({
    endpoints: (build) => ({
        getReportingSchedules: build.query<
            TPageableDataWithContentUi<TScheduleItemUI[]>,
            TPageableDataParamUI
        >({
            query({ search, pagination }) {
                return {
                    url: `${baseReportsUrl}/${EReportingUrl.SCHEDULES}`,
                    method: 'GET',
                    params: queryParamConverter(search, pagination, {}, false, true),
                };
            },
            transformResponse: (
                data: TPageableDataWithContent<TScheduleItemDB[]>
            ) => {
                const { content, ...other } = data;
                return {
                    content: data.content.map((item) =>
                        scheduleItemConverter.fromDb(item)
                    ),
                    paginationData: pageableDataConverter.fromDb(other),
                };
            },
            providesTags: ['schedules'],
        }),
        addRecipients: build.mutation<void, TAddRecipientsUI>({
            query(body) {
                return {
                    url: `${baseReportsUrl}/${EReportingUrl.RECIPIENTS}`,
                    method: 'POST',
                    body: addRecipientsConverter.toDb(body),
                };
            },
            invalidatesTags: ['schedules', 'recipientCandidates'],
        }),
        getRecipientCandidates: build.query<TRecipientCandidateUI[], TGetRecipientCandidatesParamsUI>({
            query(params) {
                return {
                    url: `${baseReportsUrl}/${EReportingUrl.RECIPIENTS}/${EReportingUrl.CANDIDATES}`,
                    method: 'GET',
                    params: { ...params },
                };
            },
            transformResponse: (data: TRecipientCandidateDB[]) =>
                data.map((item) => recipientCandidateConverter.fromDb(item)),
            providesTags: ['recipientCandidates'],
        }),
        deleteRecipient: build.mutation<void, TDeleteRecipientUI>({
            query(body) {
                return {
                    url: `${baseReportsUrl}/${EReportingUrl.RECIPIENTS}`,
                    method: 'DELETE',
                    body: deleteRecipientConverter.toDb(body),
                };
            },
            invalidatesTags: ['schedules', 'recipientCandidates'],
        }),
        editRecipientDetails: build.mutation<void, TEditRecipientDetailsUI>({
            query(body) {
                return {
                    url: `${baseReportsUrl}/${EReportingUrl.RECIPIENTS}/${EReportingUrl.SCHEDULE}`,
                    method: 'PATCH',
                    body: editRecipientDetailsConverter.toDb(body),
                };
            },
            invalidatesTags: ['schedules', 'recipientCandidates'],
        }),
        generatePetChargeReport: build.query<any, TPetChargeReportUI>({
            query(params) {
                return {
                    url: `${baseReportsUrl}/${EReportingUrl.PET_CHARGING_INFO}/${EReportingUrl.EXPORT}`,
                    method: 'GET',
                    params: generatePetChargeReportConverter.toDb(params),
                    cache: 'no-cache',
                    responseHandler: (response) => response.blob(),
                };
            },
        }),

    }),
    overrideExisting: false,
});

export const {
    useGetReportingSchedulesQuery,
    useAddRecipientsMutation,
    useGetRecipientCandidatesQuery,
    useDeleteRecipientMutation,
    useEditRecipientDetailsMutation,
    useLazyGeneratePetChargeReportQuery,
} = reportingApi;
